/**
 * Pure registration UX routing decisions (Register.js).
 * Covered by scripts/test-registration-flow-paths.js before deploy.
 */

import { OFFER_CODE_TYPES, resolveOfferCodeType } from '../constants/offerCodeTypes.js';

const BILLABLE_LEVELS = ['foundation', 'certified', 'accredited'];
const MODE_ONLINE = 'online';
const MODE_PARTNER = 'partner';
const MODE_CLOSED = 'closed';

const DEFAULT_FEE_DATA = {
  available: true,
  mode: MODE_ONLINE,
  requiresPartnerCode: false,
};

/** Whether an offer code type is allowed for the selected country × level market mode. */
export function resolveOfferMarketCompatibility({ feeData = DEFAULT_FEE_DATA, codeType }) {
  if (codeType === OFFER_CODE_TYPES.PROOF_OF_PURCHASE) {
    return { allowed: true };
  }
  if (feeData.mode === MODE_PARTNER || feeData.requiresPartnerCode) {
    return { allowed: false, errorKey: 'offer_error_need_proof' };
  }
  if (!feeData.available || feeData.mode === MODE_CLOSED) {
    return { allowed: false, errorKey: 'offer_error_discount_closed_market' };
  }
  return { allowed: true };
}

/** Step 0 Continue — which screen or advance after country + level chosen. */
export function resolveStep0ContinueAction({
  country,
  level,
  registrationsPaused = false,
  appliedOffer,
  feeData = DEFAULT_FEE_DATA,
}) {
  if (!country) {
    return { action: 'alert', alertKey: 'error_select_country' };
  }
  if (!level || level === 'notSure') {
    return { action: 'alert', alertKey: 'error_select_level' };
  }
  if (registrationsPaused) {
    return { action: 'set_step0_screen', screen: 'paused' };
  }

  if (appliedOffer) {
    const codeType = resolveOfferCodeType(appliedOffer);
    const compat = resolveOfferMarketCompatibility({ feeData, codeType });
    if (!compat.allowed) {
      return { action: 'alert', alertKey: compat.errorKey };
    }
    return { action: 'advance_to_form', startStep: 1, feeData };
  }

  if (feeData.mode === MODE_PARTNER) {
    return { action: 'set_step0_screen', screen: 'partner_gate', feeData };
  }

  if (!feeData.available || feeData.mode === MODE_CLOSED) {
    return { action: 'set_step0_screen', screen: 'market_gate', feeData };
  }

  return { action: 'advance_to_form', startStep: 1, feeData };
}

/** Offer code Apply — unified box; server validation result + current step-0 screen. */
export function resolveOfferApplyResult({ validationResult, step0Screen, feeData = DEFAULT_FEE_DATA }) {
  if (!validationResult?.valid) {
    return { action: 'reject', errorKey: 'offer_error_invalid', message: validationResult?.message };
  }

  const codeType = resolveOfferCodeType(validationResult);
  const compat = resolveOfferMarketCompatibility({ feeData, codeType });
  if (!compat.allowed) {
    return {
      action: 'reject',
      errorKey: compat.errorKey,
      message: validationResult?.message,
    };
  }

  const autoAdvanceToForm =
    step0Screen === 'market_gate' || step0Screen === 'partner_gate';

  return {
    action: 'apply',
    codeType,
    code: validationResult.code,
    autoAdvanceToForm,
  };
}

/** End of registration form → checkout view and payment choice. */
export function resolveFormSubmitCheckout({ appliedOffer, level, feeData = DEFAULT_FEE_DATA }) {
  if (appliedOffer && level === 'notSure') {
    return { action: 'alert', alertKey: 'offer_error_not_sure' };
  }

  if (appliedOffer && BILLABLE_LEVELS.includes(level)) {
    const codeType = resolveOfferCodeType(appliedOffer);
    const compat = resolveOfferMarketCompatibility({ feeData, codeType });
    if (!compat.allowed) {
      return { action: 'alert', alertKey: compat.errorKey };
    }
    return {
      action: 'goto_checkout',
      paymentChoice: codeType === OFFER_CODE_TYPES.PROOF_OF_PURCHASE ? 'partner' : 'card',
      codeType,
      usesOfferAmounts: true,
    };
  }

  if (!feeData.available || feeData.mode === MODE_CLOSED) {
    return { action: 'goto_checkout', paymentChoice: 'bespoke', codeType: null };
  }

  if (feeData.requiresPartnerCode && !appliedOffer) {
    return { action: 'goto_checkout', paymentChoice: 'partner', codeType: null, needsOfferAtCheckout: true };
  }

  return { action: 'goto_checkout', paymentChoice: 'card', codeType: null };
}

/** Checkout finalize button — Stripe vs partner activation. */
export function resolveCheckoutFinalizeAction({ paymentChoice, appliedOffer }) {
  if (paymentChoice === 'partner' && appliedOffer) {
    return {
      action: 'activate_partner_offer',
      usesStripe: false,
      paymentStatus: 'awaiting_external_settlement',
    };
  }

  if (paymentChoice === 'card') {
    return { action: 'stripe_checkout', usesStripe: true, paymentStatus: 'pending_card' };
  }

  if (paymentChoice === 'invoice') {
    return { action: 'stripe_invoice', usesStripe: true, paymentStatus: 'awaiting_invoice' };
  }

  if (paymentChoice === 'partner' && !appliedOffer) {
    return { action: 'incomplete_partner_checkout', usesStripe: false };
  }

  return { action: 'none', usesStripe: false };
}

/** Step 1 website identity check after normalising URL. */
export function resolveWebsiteIdentityCheck({ matchedDocId, matchedStatus, resumedSid }) {
  if (!matchedDocId) {
    return { action: 'advance_step' };
  }
  if (resumedSid && matchedDocId === resumedSid) {
    return { action: 'advance_step', reason: 'own_in_progress_record' };
  }
  if (matchedStatus === 'active') {
    return { action: 'goto_recovery_view' };
  }
  return { action: 'show_resumption_prompt' };
}

/** Final submit duplicate institution name check. */
export function resolveDuplicateNameCheck({ matchIds, resumedSid }) {
  const foreign = (matchIds || []).filter((id) => id !== resumedSid);
  if (foreign.length > 0) {
    return { action: 'block_duplicate' };
  }
  return { action: 'allow' };
}

/** ?sid= hydrate on register load. */
export function resolveSidHydration({ exists, status }) {
  if (!exists) {
    return { action: 'ignore' };
  }
  if (status === 'inactive') {
    return { action: 'hydrate_form', startStep: 0, clearResumptionBanner: true };
  }
  return { action: 'ignore' };
}

/** Human-readable journey label for test reports. */
export function describeRegistrationJourney(steps) {
  return steps.join(' → ');
}
